import type { CSSProperties } from "react";
import { SCENE_BASE_WIDTH, SCENE_BASE_HEIGHT } from "../sceneConstants";
import type { ElementTransform } from "./types";

export const CANVAS_W = SCENE_BASE_WIDTH;
export const CANVAS_H = SCENE_BASE_HEIGHT;

/** Smallest allowed element size, as % of canvas. */
const MIN_W_PCT = 3;
const MIN_H_PCT = 3;
/** Keep at least this fraction of the box on-canvas so it can't get lost. */
const MIN_VISIBLE_PCT = 4;

const clampNum = (n: number, min: number, max: number) =>
    Math.min(max, Math.max(min, n));

/**
 * Keep a transform sane: positive minimum size, and enough of the box on the
 * canvas to grab it again. Rotation is normalized to [0, 360).
 */
export function clampTransform(t: ElementTransform): ElementTransform {
    const width = Math.max(MIN_W_PCT, t.width);
    const height = Math.max(MIN_H_PCT, t.height);
    const x = clampNum(t.x, -width + MIN_VISIBLE_PCT, 100 - MIN_VISIBLE_PCT);
    const y = clampNum(t.y, -height + MIN_VISIBLE_PCT, 100 - MIN_VISIBLE_PCT);
    let rotation = t.rotation % 360;
    if (rotation < 0) rotation += 360;
    return { x, y, width, height, rotation };
}

/** Absolute-positioning CSS for an element box. Rotation is around the center. */
export function transformToCss(t: ElementTransform): CSSProperties {
    return {
        position: "absolute",
        left: `${t.x}%`,
        top: `${t.y}%`,
        width: `${t.width}%`,
        height: `${t.height}%`,
        transform: t.rotation ? `rotate(${t.rotation}deg)` : undefined,
        transformOrigin: "center center",
    };
}

/** Pixel aspect ratio (w/h) of a transform on the real 16:9 canvas. */
export function transformAspectRatio(t: ElementTransform): number {
    const pxW = (t.width / 100) * CANVAS_W;
    const pxH = (t.height / 100) * CANVAS_H;
    return pxH === 0 ? 1 : pxW / pxH;
}

/** Convert a pixel size on the rendered canvas into canvas-% width/height. */
export function pxSizeToPct(
    widthPx: number,
    heightPx: number,
    canvasRect: { width: number; height: number }
): { width: number; height: number } {
    return {
        width: (widthPx / canvasRect.width) * 100,
        height: (heightPx / canvasRect.height) * 100,
    };
}

/** Convert a pixel delta on the rendered canvas into a canvas-% delta. */
export function pxDeltaToPct(
    dxPx: number,
    dyPx: number,
    canvasRect: { width: number; height: number }
): { dx: number; dy: number } {
    return {
        dx: (dxPx / canvasRect.width) * 100,
        dy: (dyPx / canvasRect.height) * 100,
    };
}

/** A transform that fills the whole canvas — a full-bleed 16:9 avatar. */
export const FULLSCREEN_BOX: ElementTransform = {
    x: 0,
    y: 0,
    width: 100,
    height: 100,
    rotation: 0,
};

/** True when a box effectively fills the whole canvas (within rounding). */
export function isFullScreenBox(t: ElementTransform): boolean {
    return (
        t.x <= 0.5 &&
        t.y <= 0.5 &&
        t.width >= 99.5 &&
        t.height >= 99.5 &&
        !t.rotation
    );
}

/** A width%/height% box for the given pixel size, centered on the canvas. */
export function centeredBox(
    widthPct: number,
    heightPct: number
): ElementTransform {
    return {
        x: (100 - widthPct) / 2,
        y: (100 - heightPct) / 2,
        width: widthPct,
        height: heightPct,
        rotation: 0,
    };
}
